import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@pixlide/ui';
import {
  WEEK_DAYS,
  addDays,
  addMonths,
  describeLongDate,
  describeMonth,
  monthGrid,
  startOfMonth,
  startOfWeek,
} from '@/features/diffusion/broadcast-model';

export type DateMode = 'date' | 'period';

interface BroadcastCalendarProps {
  readonly mode: DateMode;
  readonly startsOn: string;

  /** Vide = sans date de fin. Ignoré en mode « Une date ». */
  readonly endsOn: string;

  /** Premier jour sélectionnable : on ne programme pas dans le passé. */
  readonly minDate: string;
  readonly onChange: (range: { startsOn: string; endsOn: string }) => void;
}

function NavigationBar({ label, onPrevious, onNext, canGoBack, previousLabel, nextLabel }: {
  label: string;
  onPrevious: () => void;
  onNext: () => void;
  canGoBack: boolean;
  previousLabel: string;
  nextLabel: string;
}) {
  const arrow = 'grid size-10 place-items-center rounded-xl bg-white text-ink-900 shadow-card transition hover:bg-ink-50 disabled:opacity-40';

  return (
    <div className="flex items-center justify-between rounded-xl bg-ink-50 p-1.5">
      <button type="button" className={arrow} onClick={onPrevious} disabled={!canGoBack} aria-label={previousLabel}>
        <ChevronLeft aria-hidden className="size-5" />
      </button>
      <p aria-live="polite" className="text-lg text-ink-900">
        {label}
      </p>
      <button type="button" className={arrow} onClick={onNext} aria-label={nextLabel}>
        <ChevronRight aria-hidden className="size-5" />
      </button>
    </div>
  );
}

/**
 * Choix de la date de diffusion, en deux présentations :
 * - « Une date » : bandeau d'une semaine, un clic choisit le jour ;
 * - « Une période » : mois complet, un premier clic fixe le début, un second la fin.
 *   Sans second clic, la diffusion court jusqu'à nouvel ordre.
 */
export function BroadcastCalendar({ mode, startsOn, endsOn, minDate, onChange }: BroadcastCalendarProps) {
  const [visibleWeek, setVisibleWeek] = useState(() => startOfWeek(startsOn));
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(startsOn));

  if (mode === 'date') {
    const week = WEEK_DAYS.map((_, index) => addDays(visibleWeek, index));

    return (
      <div className="flex flex-col gap-3">
        <NavigationBar
          previousLabel="Semaine précédente"
          nextLabel="Semaine suivante"
          label={describeMonth(week[3]!)}
          canGoBack={visibleWeek > minDate}
          onPrevious={() => setVisibleWeek(addDays(visibleWeek, -7))}
          onNext={() => setVisibleWeek(addDays(visibleWeek, 7))}
        />
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
          {week.map((day, index) => {
            const isSelected = day === startsOn;

            return (
              <button
                key={day}
                type="button"
                aria-pressed={isSelected}
                aria-label={describeLongDate(day)}
                disabled={day < minDate}
                onClick={() => onChange({ startsOn: day, endsOn: day })}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-xl py-3 ring-1 transition disabled:cursor-not-allowed disabled:opacity-40',
                  isSelected ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white text-ink-900 ring-ink-100 hover:ring-ink-300',
                )}
              >
                <span className="text-sm">{WEEK_DAYS[index]!.short}</span>
                <span className="text-2xl font-medium tabular-nums sm:text-3xl">{day.slice(8)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const pick = (day: string) => {
    // Une période déjà complète, ou un clic avant le début : on recommence une sélection.
    if (endsOn !== '' || day < startsOn) {
      onChange({ startsOn: day, endsOn: '' });
    } else {
      onChange({ startsOn, endsOn: day });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <NavigationBar
        previousLabel="Mois précédent"
        nextLabel="Mois suivant"
        label={describeMonth(visibleMonth)}
        canGoBack={visibleMonth > startOfMonth(minDate)}
        onPrevious={() => setVisibleMonth(addMonths(visibleMonth, -1))}
        onNext={() => setVisibleMonth(addMonths(visibleMonth, 1))}
      />

      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {WEEK_DAYS.map((day) => (
          <span key={day.bit} aria-hidden className="py-1 text-center text-xs font-medium text-ink-500 uppercase">
            {day.short}.
          </span>
        ))}

        {monthGrid(visibleMonth).map((day) => {
          const isOutsideMonth = !day.startsWith(visibleMonth.slice(0, 7));
          const isBound = day === startsOn || day === endsOn;
          const isInRange = endsOn !== '' && day > startsOn && day < endsOn;

          return (
            <button
              key={day}
              type="button"
              aria-pressed={isBound || isInRange}
              aria-label={describeLongDate(day)}
              disabled={isOutsideMonth || day < minDate}
              onClick={() => pick(day)}
              className={cn(
                'flex h-14 items-start rounded-lg p-2 text-left text-base ring-1 transition sm:h-20 sm:text-lg',
                'disabled:cursor-not-allowed disabled:bg-ink-50/60 disabled:text-ink-300 disabled:ring-ink-50',
                isBound
                  ? 'bg-ink-50 font-medium ring-2 ring-ink-900'
                  : isInRange
                    ? 'bg-ink-100/70 ring-ink-100'
                    : 'bg-white ring-ink-100 hover:ring-ink-300',
              )}
            >
              {Number(day.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}
